import {
  useWSModalCustom,
  WSAlert,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useEffect, useMemo } from "react";
import { SelectMethod } from "../../../../../Settings/components/SelectMethod";
import { useHistory } from "react-router-dom";
import { useClientQuery } from "../../../../../../query/users/queries";
import { queriesExternalBankAccounts, queriesPaymentCards } from "@query";
import { MethodValue } from "../../../../../Settings/components/ManageMethods/types";
import { Nullable } from "primereact/ts-helpers";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD } from "../../../../../Settings/paths";
import { useUserId } from "../../../../../../query/hooks/helpers";
import { selectorExternalBankAccountFundingReady } from "@selectors/selectorExternalBankAccountFundingReady";
import { useWSMutation } from "@ws-react-query";
import { WSServiceError } from "../../../../../../utils/serviceHelper";
import { usersService } from "../../../../../../services/users";
import { QUERY_USERS_CLIENT } from "../../../../../../query/users/keys";

type Props = {
  onClose: () => void;
  failedToChargeAccountId?: string;
};

const ModalEditDefaultPaymentMethod: React.FC<Props> = ({
  onClose,
  failedToChargeAccountId
}) => {
  const history = useHistory();

  const userId = useUserId();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryClient = useClientQuery(userId);
  const queryPaymentCards = queriesPaymentCards.listPaymentCards.useQuery();

  const [value, setValue] = React.useState<Nullable<MethodValue>>(null);

  const externalBankAccounts = useMemo(
    () =>
      (queryExternalBankAccounts.data?.data || []).filter(
        selectorExternalBankAccountFundingReady
      ),
    [queryExternalBankAccounts.data]
  );

  const creditCards = useMemo(
    () => queryClient.data?.profile.savedPaymentMethods,
    [queryClient.data?.profile.savedPaymentMethods]
  );

  const paymentCards = useMemo(
    () => queryPaymentCards.data?.data || [],
    [queryPaymentCards.data]
  );

  const initialValue = useMemo((): MethodValue | undefined => {
    if (queryClient.data?.profile.defaultPaymentMethod?.accountId) {
      return {
        id: queryClient.data.profile.defaultPaymentMethod.accountId,
        type: "Account"
      };
    }

    if (queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId) {
      return {
        id: queryClient.data.profile.defaultPaymentMethod.paymentMethodId,
        type: "CreditCard"
      };
    }
  }, [
    queryClient.data?.profile.defaultPaymentMethod?.accountId,
    queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId
  ]);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const [mutate, meta] = useWSMutation<void, WSServiceError, MethodValue>(
    async data => {
      if (!["Account", "CreditCard"].includes(data.type)) {
        throw new Error("Not supported");
      }

      await usersService.client.update(userId, {
        clientId: userId,
        profile: {
          defaultPaymentMethod:
            data.type === "Account"
              ? {
                  accountId: data.id,
                  paymentMethodId: null
                }
              : {
                  accountId: null,
                  paymentMethodId: data.id
                }
        }
      });
    },
    { onSuccess: onClose, dependencies: [QUERY_USERS_CLIENT] }
  );

  return (
    <WSModal
      onClose={onClose}
      title="Default payment method"
      size="S"
      shimmer={queryExternalBankAccounts.isLoading}
      actions={{
        buttons: [
          {
            label: "Save",
            kind: "Primary",
            onClick: () => {
              if (value) {
                mutate(value);
              }
            },
            loading: meta.isLoading,
            disabled: !value
          },
          {
            label: "Cancel",
            kind: "Tertiary",
            onClick: onClose
          }
        ]
      }}
    >
      <WSList gap="M">
        <WSText.ParagraphSm>
          Confirm your default payment method. This is the default method for
          you to pay for benefits and premiums through Wingspan.
        </WSText.ParagraphSm>

        <SelectMethod
          status={queryExternalBankAccounts.isLoading ? "loading" : undefined}
          data={{
            accounts: externalBankAccounts,
            creditCards,
            paymentCards
          }}
          value={value}
          onChange={setValue}
          menuFooterText="Add a new bank account"
          menuFooterAction={{
            label: "Add account",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD);
            }
          }}
        />

        {initialValue?.id === failedToChargeAccountId ? (
          <WSAlert theme="warning" title="Payment method could not be charged">
            There was an issue charging your payment method. Please select a
            different payment method or add a new one and try again.
          </WSAlert>
        ) : null}
      </WSList>
    </WSModal>
  );
};

export const useModalEditDefaultPaymentMethod = () => {
  return useWSModalCustom(ModalEditDefaultPaymentMethod);
};
